import { useT } from "@transifex/react";
import { useEffect, useState } from "react";
import CreateEditTextInputPopup from "../../../basic/popups/CreateEditTextInputPopup";
import { SchoolClassVisibilities } from "../../../enums/schoolClasses";
import { MAX_NAME_LENGTH } from "../../../constants/LimitConstants";
import {
    containsUnsupportedCharacters,
    getUniqueDefaultClassName,
    isDuplicateClassName,
    isPrivateSchoolClass,
} from "../../../utils/schoolClassUtils";
import ClassVisibilitySelector from "../ClassVisibilitySelector";
import ConsentCheckbox from "../ConsentCheckbox";

interface CreateClassPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (schoolClassName: string, classVisibility: SchoolClassVisibilities) => Promise<boolean>;
    existingClassNames: string[];
}

const CreateClassPopup = (props: CreateClassPopupProps) => {
    const t = useT();

    const t_createClass = t("Create New Class", { _tags: "SchoolClasses" });
    const t_className = t("Class Name", { _tags: "SchoolClasses, CreateClassPopup" });
    const t_create = t("Create Class", { _tags: "SchoolClasses" });
    const t_defaultClassName = t("My newest class", { _tags: "SchoolClasses" });
    const t_duplicateClassWarning = t("A class with this name already exists. Tip: Add the year.", {
        _tags: "SchoolClasses",
    });
    const t_unsupportedCharacter = t(
        "The class name contains unsupported special characters. Please use only letters, numbers, and spaces.",
        { _tags: "SchoolClasses" }
    );
    const t_classVisibility = t("Class Type", { _tags: "SchoolClasses, ClassVisibilitySelector" });
    const t_consent = t(
        "I confirm that personal data of students (e.g. name and performance data) may be processed for this private class and, if applicable, linked to phase6 accounts.",
        { _tags: "SchoolClasses, CreateClassPopup" }
    );

    const [inputValue, setInputValue] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedVisibility, setSelectedVisibility] = useState<SchoolClassVisibilities>(
        SchoolClassVisibilities.PUBLIC
    );
    const [hasConsented, setHasConsented] = useState(false);

    const isPrivate = isPrivateSchoolClass(selectedVisibility);

    useEffect(() => {
        if (props.isOpen) {
            setSelectedVisibility(SchoolClassVisibilities.PUBLIC);
            setHasConsented(false);
        }
    }, [props.isOpen]);

    const handleClose = () => {
        setErrorMessage("");
        props.onClose();
    };

    const handleSubmit = async (schoolClassName: string) => {
        const success = await props.onSubmit(schoolClassName, selectedVisibility);
        if (success) {
            props.onClose();
        }
    };

    const handleVisibilityChange = (visibility: SchoolClassVisibilities) => {
        setSelectedVisibility(visibility);
        if (visibility === SchoolClassVisibilities.PUBLIC) {
            setHasConsented(false);
        }
    };

    return (
        <CreateEditTextInputPopup
            isOpen={props.isOpen}
            onClose={handleClose}
            title={t_createClass}
            label={t_className}
            initialValue={getUniqueDefaultClassName(t_defaultClassName, props.existingClassNames)}
            primaryButton={{ text: t_create, onSubmit: handleSubmit }}
            onInputChange={(value) => {
                setInputValue(value);
                setErrorMessage(containsUnsupportedCharacters(value) ? t_unsupportedCharacter : "");
            }}
            warningMessage={
                isDuplicateClassName({ name: inputValue, existingNames: props.existingClassNames })
                    ? t_duplicateClassWarning
                    : ""
            }
            alertMessage={errorMessage}
            isDisabled={isPrivate && !hasConsented}
            maxLength={MAX_NAME_LENGTH}
        >
            <div className="flex flex-col gap-xs">
                <span className="text-l text-p6-grey-dark">{t_classVisibility}</span>
                <ClassVisibilitySelector
                    selectedVisibility={selectedVisibility}
                    onVisibilityChange={handleVisibilityChange}
                />
            </div>

            {isPrivate && (
                <ConsentCheckbox
                    isChecked={hasConsented}
                    onChange={() => setHasConsented(!hasConsented)}
                    label={t_consent}
                />
            )}
        </CreateEditTextInputPopup>
    );
};

export default CreateClassPopup;
